// ============================================================
// 店舗ごとの業務ルール（2026-10-06 前田さん「宮古は別の config を持っている方がいい」）
//
// エージェントのプロンプト・ツール・AI メールの基本ルールは、ここから店舗の値を読む。
// 那覇の値は 2026-09 までの運用（記録#28/#29・フリート割）そのまま。宮古の値は前田さんの回答による:
//   - キャンセルポリシー: 那覇と同じ（ただし別に持つ）
//   - 送迎: 現状は送迎プランのみ。セルフプランが入っても対応できるようにする
//   - フリート割・ガチャ枠: あり
//   - Notion（事故報告・車両情報）: 宮古は使わない
// 料金・補償単価・深夜早朝手数料の正は booking-platform/config/commerce.json（ここには書かない）。
// ============================================================
import { getStoreKey } from './backend';

export type StoreKey = 'ncar' | 'pit';

export interface StorePolicy {
  key: StoreKey;
  label: string;
  /** キャンセル料（出発日からの日数ごと） */
  cancellationPolicy: Array<{ when: string; fee: string }>;
  /** 送迎の運用 */
  pickup: {
    /** 原則の案内（「送迎はありますか」への答え） */
    standard: string;
    /** プラン名にこの文字列があればセルフプラン */
    selfPlanKeyword: string;
    /** セルフプランの案内文 */
    selfPlanGuidance: string;
    /** 補足（現状の運用など） */
    note?: string;
  };
  /** フリート割（合計台数で判定。全車の基本料金合計に掛け、請求書にだけ反映する） */
  fleetDiscount: Array<{ minVehicles: number; rate: number }>;
  /** フリート割の対象外の車種コード（台数にも数えず、割引もしない）。2026-10-19 前田さん: コンパクトカーとアルファード20系 */
  fleetExcludedCodes: string[];
  /** ガチャ枠: 予約の car_type（コード）→ 振り替えてよい車種コード */
  gatchaPools: Record<string, string[]>;
  /** 満車時の無料アップグレード先（同系統の上位版だけ。別車種は入れない） */
  upgradePools: Record<string, string[]>;
  /** 深夜・早朝の対応（深夜早朝 OK テンプレ・時間帯の注意を出すか） */
  nightService: boolean;
  /** 社内 Notion（事故報告・車両情報）を使うか */
  notion: boolean;
}

const CANCELLATION_POLICY = [
  { when: 'ご出発の7日〜6日前', fee: '総額の30%' },
  { when: 'ご出発の5日〜3日前', fee: '総額の50%' },
  { when: 'ご出発の2日〜前日', fee: '総額の80%' },
  { when: 'ご出発の当日', fee: '総額の100%' },
  { when: '無連絡キャンセル', fee: '総額の100%' },
];

const SELF_PLAN_GUIDANCE = 'セルフプランは代表者に来店していただき送迎していただくプランですが、ご希望でしたら送迎も可能です。'
  + '当日空港到着後にご連絡の上、リクエストしてください。'
  + '送迎に関するお時間のお約束はできかねますのであらかじめご了承ください。';

// 憲法（booking-platform/md/multi-vehicle.md・pricing.md）: **3台以上で5%**（全車の基本料金合計×5%・請求書にだけ反映）。
// 2026-10-19 まではエージェントのプロンプトが「5台以上10%」としていて憲法と矛盾していた
const FLEET_DISCOUNT = [
  { minVehicles: 3, rate: 0.05 },
];
// 別車種の組み合わせもフリート割の対象（2026-10-19 前田さん）。ただしコンパクトカーとアルファード20系は対象外
// コンパクトクラスA（CP-A・那覇のヤリスクロス）は対象（2026-11-01）。コンパクトクラスB（CP-VN-F）は未決のため対象外のまま
const FLEET_EXCLUDED = ['CP-VN-F', 'T-AV-M'];

export const STORE_POLICIES: Record<StoreKey, StorePolicy> = {
  ncar: {
    key: 'ncar',
    label: '那覇空港店',
    cancellationPolicy: CANCELLATION_POLICY,
    pickup: {
      standard: '原則、送迎はあり',
      selfPlanKeyword: 'セルフ',
      selfPlanGuidance: SELF_PLAN_GUIDANCE,
    },
    fleetDiscount: FLEET_DISCOUNT,
    fleetExcludedCodes: FLEET_EXCLUDED,
    // 記録#28（2026-09-08）
    gatchaPools: {
      Gatcha: ['T-AV-M', 'T-AV-H', 'M-D5-H'],
      GatchaH: ['T-AV-H', 'M-D5-H'],
      // ガチャ低（じゃらん「ファミリータイプ」2026-10-24）。車は通常ガチャと同じ
      GatchaL: ['T-AV-M', 'T-AV-H', 'M-D5-H'],
    },
    // 記録#30（2026-09-19）: 20系 → 30系 は同系統の上位版なので無断でよい。デリカは別車種なので入れない
    upgradePools: { 'T-AV-M': ['T-AV-H'] },
    nightService: true,
    notion: true,
  },
  pit: {
    key: 'pit',
    label: '宮古空港店',
    // 那覇と同じ規定（2026-10-06 前田さん）。店舗で変わり得るので別に持つ
    cancellationPolicy: CANCELLATION_POLICY.map((r) => ({ ...r })),
    pickup: {
      standard: '原則、送迎はあり',
      selfPlanKeyword: 'セルフ',
      selfPlanGuidance: SELF_PLAN_GUIDANCE,
      note: '宮古は現状すべて送迎プラン。プラン名に「セルフ」があるときだけセルフプランとして案内する',
    },
    // フリート割はあり（2026-10-06）。率は那覇と同じ＝憲法の3台以上5%（違う場合はここを直す）
    fleetDiscount: FLEET_DISCOUNT.map((r) => ({ ...r })),
    fleetExcludedCodes: [...FLEET_EXCLUDED],
    // ガチャ枠はあり（2026-10-06）。宮古の車種はデリカD:5・アルファード20系・日産ノート・エブリーだけなので、
    // 那覇の定義のうち宮古にある車種に絞った。GatchaH（高級ガチャ）は30系が無いためデリカのみ（要確認）
    gatchaPools: {
      Gatcha: ['T-AV-M', 'M-D5-H'],
      GatchaH: ['M-D5-H'],
      GatchaL: ['T-AV-M', 'M-D5-H'],
    },
    // 宮古には同系統の上位版（アルファード30系）が無い
    upgradePools: {},
    nightService: false,
    notion: false,
  },
};

export function getStorePolicy(store: StoreKey = getStoreKey()): StorePolicy {
  return STORE_POLICIES[store] ?? STORE_POLICIES.ncar;
}

/** 合計台数に対するフリート割の率（該当なしは 0） */
export function fleetDiscountRate(totalVehicles: number, store: StoreKey = getStoreKey()): number {
  return getStorePolicy(store).fleetDiscount
    .filter((r) => totalVehicles >= r.minVehicles)
    .reduce((acc, r) => Math.max(acc, r.rate), 0);
}

/** プラン名からセルフプランか */
export function isSelfPlan(plan: string | null | undefined, store: StoreKey = getStoreKey()): boolean {
  return new RegExp(getStorePolicy(store).pickup.selfPlanKeyword).test(String(plan ?? ''));
}

/**
 * 複数台グループのフリート割（2026-10-19）。対象外の車種は台数にも数えず、割引もしない。
 * @param units 各台の車種コードと基本料金（割引前）
 */
export function fleetDiscountFor(units: Array<{ code: string; base: number }>, store: StoreKey = getStoreKey()):
  { eligibleCount: number; rate: number; amount: number; excludedCount: number } {
  const pol = getStorePolicy(store);
  const eligible = units.filter((u) => !pol.fleetExcludedCodes.includes(u.code));
  const rate = fleetDiscountRate(eligible.length, store);
  const amount = Math.floor(eligible.reduce((x, u) => x + u.base, 0) * rate);
  return { eligibleCount: eligible.length, rate, amount, excludedCount: units.length - eligible.length };
}
